import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faSpinner,
  faEye,
  faFilePdf,
  faDownload,
  faExclamationTriangle,
  faBan,
  faInfoCircle,
  faSync,
  faUpload,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import axios from "axios";
import { BASE_URL } from "../../config";
import CancelSeriesModalAdminFee from "./CancelSeriesModalAdminFee";
import ViewCancellationModal from "./ViewCancellationModal";
import * as XLSX from "xlsx";

const TEMPLATE_URL =
  "https://public-testing.ap-south-1.linodeobjects.com/delivery-date-template.xlsx";

const AdminFeeHistory = () => {
  // State for filters
  const [filters, setFilters] = useState({
    billingType: "SI",
    seriesNo: "",
    dateFrom: "",
    dateTo: "",
    corporate: [],
    errorStatus: "all",
    cancelStatus: "all",
  });

  // State for corporate search
  const [corporateSearch, setCorporateSearch] = useState("");
  const [filteredCorporations, setFilteredCorporations] = useState([]);

  // State for table
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [seriesData, setSeriesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(null);

  // State for errors modal
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorSeriesNoUid, setErrorSeriesNoUid] = useState(null);
  const [errorData, setErrorData] = useState([]);
  const [errorPage, setErrorPage] = useState(1);
  const [errorPerPage, setErrorPerPage] = useState(10);
  const [errorTotalPages, setErrorTotalPages] = useState(1);
  const [errorTotalEntries, setErrorTotalEntries] = useState(0);
  const [errorLoading, setErrorLoading] = useState(false);

  // State for cancel modal
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelSeriesNo, setCancelSeriesNo] = useState(null);
  const [cancelSeriesNoUid, setCancelSeriesNoUid] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  // State for view cancellation modal
  const [viewCancelModalOpen, setViewCancelModalOpen] = useState(false);
  const [cancellationDetails, setCancellationDetails] = useState(null);
  const [viewCancelSeriesNo, setViewCancelSeriesNo] = useState(null);
  const [viewCancelLoading, setViewCancelLoading] = useState(false);

  // State for row selection and batch upload
  const [selectedRows, setSelectedRows] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [successfulUploads, setSuccessfulUploads] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const allowedFileTypes = ["xlsx", "xls"];
  const maxFileSizeMB = 20;
  const maxUploadLimit = 5;

  // Add state for all series data
  const [allSeriesData, setAllSeriesData] = useState([]);

  // Formatter for currency
  const phpFormatter = new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Handle row selection
  const handleRowSelect = (series) => {
    setSelectedRows((prev) => {
      if (prev.some((row) => row.series_no_uid === series.series_no_uid)) {
        return prev.filter((row) => row.series_no_uid !== series.series_no_uid);
      }
      return [...prev, series];
    });
  };

  // Handle select all rows
  // const handleSelectAll = (e) => {
  //   if (e.target.checked) {
  //     const newSelections = seriesData.filter(
  //       (series) =>
  //         !selectedRows.some(
  //           (row) => row.series_no_uid === series.series_no_uid
  //         )
  //     );
  //     setSelectedRows((prev) => [...prev, ...newSelections]);
  //   } else {
  //     const currentPageIds = seriesData.map((series) => series.series_no_uid);
  //     setSelectedRows((prev) =>
  //       prev.filter((row) => !currentPageIds.includes(row.series_no_uid))
  //     );
  //   }
  // };

  // Update handleSelectAll to fetch and select all series across all pages
  const handleSelectAll = async (e) => {
    if (e.target.checked) {
      try {
        setLoading(true);
        const response = await axios.get(`${BASE_URL}/admin-fee/history`, {
          params: {
            billing_type: filters.billingType || undefined,
            series_no: filters.seriesNo || undefined,
            date_from: filters.dateFrom || undefined,
            date_to: filters.dateTo || undefined,
            corporate: filters.corporate.map((c) => c.id),
            error_status:
              filters.errorStatus !== "all" ? filters.errorStatus : undefined,
            cancel_status:
              filters.cancelStatus !== "all" ? filters.cancelStatus : undefined,
            per_page: 10000, // Large number to fetch all records
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        });

        // const seriesWithErrors = await Promise.all(
        //   response.data.data.map(async (series) => {
        //     const errorCountResponse = await axios.get(
        //       `${BASE_URL}/admin-fee/series/${series.series_no_uid}/errors`,
        //       {
        //         params: { per_page: 1 },
        //         headers: {
        //           Authorization: `Bearer ${localStorage.getItem(
        //             "authentication/token"
        //           )}`,
        //         },
        //       }
        //     );

        //     // const corporateResponse = await axios.get(
        //     //   `${BASE_URL}/admin-fee/series/${series.series_no_uid}/corporates`,
        //     //   {
        //     //     headers: {
        //     //       Authorization: `Bearer ${localStorage.getItem(
        //     //         "authentication/token"
        //     //       )}`,
        //     //     },
        //     //   }
        //     // );
            
        //     return {
        //       ...series,
        //       error_count: errorCountResponse.data.pagination.total_records,
        //       // corporate_name: corporateResponse.data.data
        //       //   .map((corp) => corp.name)
        //       //   .join(", "),
        //     };
        //   })
        // );

        setAllSeriesData(response.data.data);
        setSelectedRows(response.data.data); // Select all series
      } catch (error) {
        console.error(
          "Select All Error:",
          error.response?.data?.resolved_sql_query || ""
        );
        toast.error("Failed to select all series.", { duration: 4000 });
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedRows([]); // Deselect all
      setAllSeriesData([]);
    }
  };

  // Download selected series template
  const handleDownloadTemplate = () => {
    if (selectedRows.length === 0) {
      toast.error("Please select at least one series to download.", {
        duration: 4000,
      });
      return;
    }

    const data = selectedRows.map((row) => ({
      "Series Unique ID": row.series_no_uid,
      "Series No": row.series_no,
      "Date Delivered": "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Selected Series");

    worksheet["!cols"] = [{ wch: 36 }, { wch: 15 }, { wch: 15 }];

    XLSX.writeFile(
      workbook,
      `Selected_Series_Template_${new Date()
        .toISOString()
        .replace(/[:.]/g, "-")}.xlsx`
    );
    toast.success("Template downloaded successfully.", { duration: 4000 });
  };

  // Open batch upload modal
  const handleBatchUpload = () => {
    setUploadModalOpen(true);
    setFiles([]);
    setSuccessfulUploads([]);
    setIsDragging(false);
  };

  // Handle file selection for upload
  const handleFileChange = (selectedFiles) => {
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const isAllowedType = allowedFileTypes.includes(fileExtension);
      const isWithinSizeLimit = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!isAllowedType) {
        toast.error(
          `File ${file.name} is not supported. Only ${allowedFileTypes.join(
            ", "
          )} files allowed.`,
          { duration: 4000 }
        );
        return false;
      }

      if (!isWithinSizeLimit) {
        toast.error(
          `File ${file.name} exceeds ${maxFileSizeMB}MB size limit.`,
          { duration: 4000 }
        );
        return false;
      }

      return true;
    });

    if (files.length + validFiles.length > maxUploadLimit) {
      toast.error(
        `You can only upload up to ${maxUploadLimit} files at a time.`,
        { duration: 4000 }
      );
      return;
    }

    const newFiles = validFiles.map((file) => ({
      file,
      progress: 0,
      isUploading: false,
      isPolling: false,
      uploadId: null,
      error: null,
      status: null,
      summary: null,
    }));

    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    newFiles.forEach((fileObj) => handleFileUpload(fileObj));
  };

  // Handle file upload
  const handleFileUpload = async (fileObj) => {
    setFiles((prevFiles) =>
      prevFiles.map((f) =>
        f.file === fileObj.file ? { ...f, isUploading: true } : f
      )
    );
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", fileObj.file);

      const response = await axios.post(
        `${BASE_URL}/admin-fee/file-upload/delivery-date`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file
                  ? { ...f, progress: percentCompleted }
                  : f
              )
            );
          },
        }
      );

      const uploadId = response.data.upload_id;
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? {
                ...f,
                isUploading: false,
                isPolling: true,
                progress: 100,
                uploadId,
              }
            : f
        )
      );
      setSuccessfulUploads((prev) => [...prev, uploadId]);
      toast.success(`File ${fileObj.file.name} uploaded successfully!`, {
        duration: 4000,
      });

      let attempts = 0;
      const maxAttempts = 60;
      const pollInterval = 3000;

      const pollStatus = async () => {
        try {
          const statusResponse = await axios.get(
            `${BASE_URL}/admin-fee/file-upload/delivery-upload-status`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "authentication/token"
                )}`,
              },
              params: { upload_id: uploadId },
            }
          );

          const upload = statusResponse.data.data;
          if (upload.status === "completed" || upload.status === "failed") {
            setFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file
                  ? {
                      ...f,
                      isPolling: false,
                      status: upload.status,
                      summary: {
                        successCount: upload.success_update_count || 0,
                        failedCount: upload.failed_update_count || 0,
                        errorMessage: upload.error_message || null,
                      },
                    }
                  : f
              )
            );
            if (upload.status === "completed") {
              toast.success(
                `File ${fileObj.file.name} processed successfully!`,
                { duration: 4000 }
              );
              fetchSeriesHistory();
            } else {
              toast.error(
                `File ${fileObj.file.name} processing failed: ${
                  upload.error_message || "Unknown error"
                }`,
                { duration: 4000 }
              );
            }
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(pollStatus, pollInterval);
          } else {
            setFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file
                  ? {
                      ...f,
                      isPolling: false,
                      status: "timeout",
                      summary: { errorMessage: "Upload processing timed out." },
                    }
                  : f
              )
            );
            toast.error(`File ${fileObj.file.name} processing timed out.`, {
              duration: 4000,
            });
          }
        } catch (error) {
          if (attempts < maxAttempts) {
            attempts++;
            setTimeout(pollStatus, pollInterval);
          } else {
            setFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file
                  ? {
                      ...f,
                      isPolling: false,
                      status: "error",
                      summary: {
                        errorMessage: "Error checking upload status.",
                      },
                    }
                  : f
              )
            );
            toast.error(`Error checking status for ${fileObj.file.name}.`, {
              duration: 4000,
            });
          }
        }
      };

      setTimeout(pollStatus, pollInterval);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to upload file.";
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? {
                ...f,
                isUploading: false,
                isPolling: false,
                error: errorMessage,
              }
            : f
        )
      );
      toast.error(errorMessage, { duration: 4000 });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle drag and drop events
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    handleFileChange(droppedFiles);
  };

  // Remove uploaded file
  const handleRemoveFile = async (fileObj) => {
    const result = await Swal.fire({
      title: "Remove File?",
      text: `Are you sure you want to remove ${fileObj.file.name}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, remove it",
    });

    if (result.isConfirmed) {
      setFiles((prevFiles) => prevFiles.filter((f) => f.file !== fileObj.file));
      if (fileObj.uploadId) {
        setSuccessfulUploads((prev) =>
          prev.filter((id) => id !== fileObj.uploadId)
        );
      }
      toast.success("File removed successfully.", { duration: 3000 });
    }
  };

  // Trigger file input
  const handleChooseFile = () => {
    fileInputRef.current.click();
  };

  // Fetch corporations
  const fetchCorporations = async (search = "") => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/admin-fee/corporations`, {
        params: { search, billing_type: filters.billingType || undefined },
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      });
      console.log("Corporations Query:", response.data.resolved_sql_query);
      setFilteredCorporations(response.data.data);
    } catch (error) {
      console.error(
        "Corporations Error:",
        error.response?.data?.resolved_sql_query || ""
      );
      toast.error("Failed to fetch corporations.", { duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  // Handle corporate search
  const handleCorporateSearch = (e) => {
    const value = e.target.value;
    setCorporateSearch(value);
    fetchCorporations(value);
  };

  // Handle corporate selection
  const handleCorporateSelect = (corp) => {
    if (!filters.corporate.some((c) => c.id === corp.id)) {
      setFilters((prev) => ({
        ...prev,
        corporate: [...prev.corporate, corp],
      }));
      setCorporateSearch("");
      setFilteredCorporations([]);
    }
  };

  // Remove corporate filter
  const handleRemoveCorporate = (corpId) => {
    setFilters((prev) => ({
      ...prev,
      corporate: prev.corporate.filter((c) => c.id !== corpId),
    }));
  };

  // Fetch series history
  // const fetchSeriesHistory = async () => {
  //   try {
  //     setLoading(true);
  //     const response = await axios.get(`${BASE_URL}/admin-fee/history`, {
  //       params: {
  //         billing_type: filters.billingType || undefined,
  //         series_no: filters.seriesNo || undefined,
  //         date_from: filters.dateFrom || undefined,
  //         date_to: filters.dateTo || undefined,
  //         corporate: filters.corporate.map((c) => c.id),
  //         error_status: filters.errorStatus !== 'all' ? filters.errorStatus : undefined,
  //         cancel_status: filters.cancelStatus !== 'all' ? filters.cancelStatus : undefined,
  //         page: currentPage,
  //         per_page: itemsPerPage,
  //       },
  //       headers: {
  //         Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
  //       },
  //     });

  //     const seriesWithErrors = await Promise.all(
  //       response.data.data.map(async (series) => {
  //         const errorCountResponse = await axios.get(`${BASE_URL}/admin-fee/series/${series.series_no_uid}/errors`, {
  //           params: { per_page: 1 },
  //           headers: {
  //             Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
  //           },
  //         });
  //         return {
  //           ...series,
  //           error_count: errorCountResponse.data.pagination.total_records,
  //         };
  //       })
  //     );

  //     setSeriesData(seriesWithErrors);
  //     setTotalEntries(response.data.pagination.total_records);
  //     setTotalPages(response.data.pagination.total_pages);
  //   } catch (error) {
  //     console.error('Series History Error:', error.response?.data?.resolved_sql_query || '');
  //     toast.error('Failed to fetch series history.', { duration: 4000 });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Update fetchSeriesHistory to include corporate names
  const fetchSeriesHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/admin-fee/history`, {
        params: {
          billing_type: filters.billingType || undefined,
          series_no: filters.seriesNo || undefined,
          date_from: filters.dateFrom || undefined,
          date_to: filters.dateTo || undefined,
          corporate: filters.corporate.map((c) => c.id),
          error_status:
            filters.errorStatus !== "all" ? filters.errorStatus : undefined,
          cancel_status:
            filters.cancelStatus !== "all" ? filters.cancelStatus : undefined,
          page: currentPage,
          per_page: itemsPerPage,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      });

      // const seriesWithErrors = await Promise.all(
      //   response.data.data.map(async (series) => {
      //     const errorCountResponse = await axios.get(
      //       `${BASE_URL}/admin-fee/series/${series.series_no_uid}/errors`,
      //       {
      //         params: { per_page: 1 },
      //         headers: {
      //           Authorization: `Bearer ${localStorage.getItem(
      //             "authentication/token"
      //           )}`,
      //         },
      //       }
      //     );
      //     // Fetch corporate names for the series
      //     // const corporateResponse = await axios.get(
      //     //   `${BASE_URL}/admin-fee/series/${series.series_no_uid}/corporates`,
      //     //   {
      //     //     headers: {
      //     //       Authorization: `Bearer ${localStorage.getItem(
      //     //         "authentication/token"
      //     //       )}`,
      //     //     },
      //     //   }
      //     // );
      //     return {
      //       ...series,
      //       error_count: errorCountResponse.data.pagination.total_records
      //       // ,
      //       // corporate_name: corporateResponse.data.data
      //       //   .map((corp) => corp.name)
      //       //   .join(", "), // Join multiple corporate names
      //     };
      //   })
      // );

      setSeriesData(response.data.data);
      setTotalEntries(response.data.pagination.total_records);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      console.error(
        "Series History Error:",
        error.response?.data?.resolved_sql_query || ""
      );
      toast.error("Failed to fetch series history.", { duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  // Fetch errors for a series
  const fetchSeriesErrors = async (seriesNoUid) => {
    try {
      setErrorLoading(true);
      const response = await axios.get(
        `${BASE_URL}/admin-fee/series/${seriesNoUid}/errors`,
        {
          params: {
            page: errorPage,
            per_page: errorPerPage,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      console.log("Series Errors Queries:", response.data.resolved_sql_query);
      setErrorData(response.data.data);
      setErrorTotalEntries(response.data.pagination.total_records);
      setErrorTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      console.error(
        "Series Errors Error:",
        error.response?.data?.resolved_sql_query || ""
      );
      toast.error("Failed to fetch series errors.", { duration: 4000 });
    } finally {
      setErrorLoading(false);
    }
  };

  // Open errors modal
  const openErrorModal = (seriesNoUid) => {
    setErrorSeriesNoUid(seriesNoUid);
    setErrorPage(1);
    setErrorModalOpen(true);
    fetchSeriesErrors(seriesNoUid);
  };

  // Handle PDF download
  const handleDownloadPdfs = async (seriesNoUid, seriesNo) => {
    try {
      setPdfLoading(seriesNoUid);
      const response = await axios.get(
        `${BASE_URL}/admin-fee/series/${seriesNoUid}/pdfs`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/zip" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `AdminFee_Series_${seriesNo}_${new Date()
          .toISOString()
          .replace(/[:.]/g, "-")}.zip`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("PDFs and XLSX downloaded successfully.", {
        duration: 4000,
      });
    } catch (error) {
      console.error("PDFs Download Error:", error);
      toast.error("Failed to download files.", { duration: 4000 });
    } finally {
      setPdfLoading(null);
    }
  };

  // Handle cancel series
  const handleCancelSeries = async (seriesNoUid, reason) => {
    const result = await Swal.fire({
      title: `Are you sure you want to cancel Series Number: ${cancelSeriesNo}?`,
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, cancel it!",
      cancelButtonText: "No, keep it",
    });

    if (!result.isConfirmed) return;

    try {
      setCancelLoading(true);
      await axios.post(
        `${BASE_URL}/admin-fee/series/${seriesNoUid}/cancel`,
        { reason },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      toast.success("Series cancelled successfully.", { duration: 4000 });
      fetchSeriesHistory();
      setCancelModalOpen(false);
    } catch (error) {
      console.error("Cancel Series Error:", error);
      toast.error("Failed to cancel series.", { duration: 4000 });
    } finally {
      setCancelLoading(false);
    }
  };

  // Handle view cancellation details
  const handleViewCancellation = async (seriesNo, seriesNoUid) => {
    try {
      setViewCancelLoading(true);
      const response = await axios.get(
        `${BASE_URL}/admin-fee/series/${seriesNoUid}/cancellation-details`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      setCancellationDetails(response.data.data);
      setViewCancelSeriesNo(seriesNo);
      setViewCancelModalOpen(true);
    } catch (error) {
      console.error("View Cancellation Error:", error);
      toast.error("Failed to fetch cancellation details.", { duration: 4000 });
    } finally {
      setViewCancelLoading(false);
    }
  };

  // Open cancel modal
  const openCancelModal = (seriesNo, seriesNoUid) => {
    setCancelSeriesNo(seriesNo);
    setCancelSeriesNoUid(seriesNoUid);
    setCancelModalOpen(true);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Fetch data when filters or pagination change
  useEffect(() => {
    fetchSeriesHistory();
  }, [filters, currentPage, itemsPerPage]);

  // Fetch errors when error modal page changes
  useEffect(() => {
    if (errorModalOpen && errorSeriesNoUid) {
      fetchSeriesErrors(errorSeriesNoUid);
    }
  }, [errorPage, errorPerPage]);

  // Table columns
  // const columns = [
  //   { key: 'select', label: <input type="checkbox" onChange={handleSelectAll} checked={seriesData.every((series) => selectedRows.some((row) => row.series_no_uid === series.series_no_uid))} />, sortable: false },
  //   { key: 'series_no', label: 'Series No.', sortable: true },
  //   { key: 'transaction_count', label: 'Transaction Count', sortable: true },
  //   { key: 'billing_type', label: 'Billing Type', sortable: true },
  //   { key: 'total_vat_amount', label: 'Total VAT', sortable: true },
  //   { key: 'total_rate_amount', label: 'Total Rate', sortable: true },
  //   { key: 'total_admin_fee_amount', label: 'Total Admin Fee', sortable: true },
  //   { key: 'date_created', label: 'Date Created', sortable: true },
  //   { key: 'delivery_date', label: 'Date Delivered', sortable: true },
  //   { key: 'actions', label: 'Actions', sortable: false },
  // ];

  // Add new column to columns array
  // const columns = [
  //   {
  //     key: "select",
  //     label: (
  //       <input
  //         type="checkbox"
  //         onChange={handleSelectAll}
  //         checked={seriesData.every((series) =>
  //           selectedRows.some(
  //             (row) => row.series_no_uid === series.series_no_uid
  //           )
  //         )}
  //       />
  //     ),
  //     sortable: false,
  //   },
  //   { key: "series_no", label: "Series No.", sortable: true },
  //   { key: "transaction_count", label: "Transaction Count", sortable: true },
  //   { key: "billing_type", label: "Billing Type", sortable: true },
  //   { key: "corp_names", label: "Corporate Name", sortable: true }, // New column
  //   { key: "total_vat_amount", label: "Total VAT", sortable: true },
  //   { key: "total_rate_amount", label: "Total Rate", sortable: true },
  //   { key: "total_admin_fee_amount", label: "Total Admin Fee", sortable: true },
  //   { key: "date_created", label: "Date Created", sortable: true },
  //   { key: "delivery_date", label: "Date Delivered", sortable: true },
  //   { key: "actions", label: "Actions", sortable: false },
  // ];

  // Update columns to reflect select all state
  const columns = [
    {
      key: "select",
      label: (
        <input
          type="checkbox"
          onChange={handleSelectAll}
          checked={
            allSeriesData.length > 0 &&
            selectedRows.length === allSeriesData.length
          }
        />
      ),
      sortable: false,
    },
    { key: "series_no", label: "Series No.", sortable: true },
    { key: "transaction_count", label: "Transaction Count", sortable: true },
    { key: "billing_type", label: "Billing Type", sortable: true },
    { key: "corp_names", label: "Corporate Name", sortable: true },

    // { key: "total_rate_amount", label: "(NET) Total Rate", sortable: true },
    // { key: "total_vat_amount", label: "(VAT) Total VAT", sortable: true },
    // { key: "total_admin_fee_amount", label: "(GROSS) Total Admin Fee", sortable: true },
    {
      key: 'total_rate_amount',
      label: (
        <span className="inline-flex items-center">
          <span className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full">
            NET
          </span>
          Total Rate
        </span>
      ),
      sortable: true,
    },
    {
      key: 'total_vat_amount',
      label: (
        <span className="inline-flex items-center">
          <span className="bg-green-100 text-green-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full">
            VAT
          </span>
          Total VAT
        </span>
      ),
      sortable: true,
    },
    {
      key: 'total_admin_fee_amount',
      label: (
        <span className="inline-flex items-center">
          <span className="bg-purple-100 text-purple-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full">
            GROSS
          </span>
          Total Admin Fee
        </span>
      ),
      sortable: true,
    },

    { key: "date_created", label: "Date Created", sortable: true },
    { key: "delivery_date", label: "Date Delivered", sortable: true },
    { key: "actions", label: "Actions", sortable: false },
  ];

  // Error table columns
  const errorColumns = [
    { key: "series_no", label: "Series No." },
    { key: "corp_code", label: "Corp Code" },
    { key: "member_id", label: "Member ID" },
    { key: "member_name", label: "Member Name" },
    { key: "member_type", label: "Member Type" },
    { key: "error_message", label: "Error Message" },
    { key: "failed_at", label: "Failed At" },
  ];

  // Pagination for main table
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  // Pagination for error modal
  const handleErrorPageChange = (page) => {
    if (page >= 1 && page <= errorTotalPages) setErrorPage(page);
  };

  const handleErrorItemsPerPageChange = (value) => {
    setErrorPerPage(value);
    setErrorPage(1);
  };

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 3) endPage = Math.min(5, totalPages);
    else if (currentPage >= totalPages - 2)
      startPage = Math.max(totalPages - 4, 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg text-sm transition-all duration-200 ${
            currentPage === i
              ? "bg-indigo-600 text-white"
              : "text-gray-700 hover:bg-indigo-100"
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const renderErrorPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, errorPage - 2);
    let endPage = Math.min(errorTotalPages, errorPage + 2);
    if (errorPage <= 3) endPage = Math.min(5, errorTotalPages);
    else if (errorPage >= errorTotalPages - 2)
      startPage = Math.max(errorTotalPages - 4, 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handleErrorPageChange(i)}
          className={`px-3 py-1 rounded-lg text-sm transition-all duration-200 ${
            errorPage === i
              ? "bg-indigo-600 text-white"
              : "text-gray-700 hover:bg-indigo-100"
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const getShowingEntriesText = () => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalEntries);
    return `Showing ${start} to ${end} of ${totalEntries.toLocaleString()} series`;
  };

  const getErrorShowingEntriesText = () => {
    const start = (errorPage - 1) * errorPerPage + 1;
    const end = Math.min(errorPage * errorPerPage, errorTotalEntries);
    return `Showing ${start} to ${end} of ${errorTotalEntries.toLocaleString()} errors`;
  };

  // Loading Skeleton
  const LoadingSkeleton = ({ rows = 5, columns = 8 }) => (
    <>
      {Array(rows)
        .fill()
        .map((_, i) => (
          <tr key={i} className="animate-pulse border-b">
            {Array(columns)
              .fill()
              .map((_, j) => (
                <td key={j} className="p-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </td>
              ))}
          </tr>
        ))}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <Tooltip id="filter-tooltip" />
      <div className="max-w-1xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-white rounded-2xl shadow-lg">
            <FontAwesomeIcon
              icon={faSearch}
              className="text-indigo-600 text-lg"
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Admin Fee (Billed)
            </h1>
            <p className="text-sm text-gray-500">
              View and manage billing series
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Filters Section */}
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Filters
            </h2>
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px] flex flex-col gap-1 relative">
                <label
                  htmlFor="corporate"
                  className="text-sm font-medium text-gray-700"
                >
                  Corporate
                </label>
                <input
                  id="corporate"
                  type="text"
                  placeholder="Search corporations..."
                  value={corporateSearch}
                  onChange={handleCorporateSearch}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-tooltip-id="filter-tooltip"
                  data-tooltip-content="Search and select corporations"
                />
                {filteredCorporations.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 max-h-40 overflow-y-auto shadow-lg top-full">
                    {filteredCorporations.map((corp) => (
                      <li
                        key={corp.id}
                        onClick={() => handleCorporateSelect(corp)}
                        className="px-3 py-2 text-sm hover:bg-indigo-100 cursor-pointer"
                      >
                        {corp.id} - {corp.name}
                      </li>
                    ))}
                  </ul>
                )}
                {filters.corporate.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {filters.corporate.map((corp) => (
                      <span
                        key={corp.id}
                        className="inline-flex items-center px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded-full"
                      >
                        {corp.name}
                        <button
                          onClick={() => handleRemoveCorporate(corp.id)}
                          className="ml-1 text-indigo-600 hover:text-indigo-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-[150px] flex flex-col gap-1">
                <label
                  htmlFor="billingType"
                  className="text-sm font-medium text-gray-700"
                >
                  Billing Type
                </label>
                <select
                  id="billingType"
                  value={filters.billingType}
                  onChange={(e) =>
                    handleFilterChange("billingType", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-tooltip-id="filter-tooltip"
                  data-tooltip-content="Filter by billing type"
                >
                  <option value="SI">Sales Invoice (SI)</option>
                  <option value="SOA">Statement of Account (SOA)</option>
                </select>
              </div>

              <div className="flex-1 min-w-[150px] flex flex-col gap-1">
                <label
                  htmlFor="seriesNo"
                  className="text-sm font-medium text-gray-700"
                >
                  Series Number
                </label>
                <input
                  id="seriesNo"
                  type="number"
                  value={filters.seriesNo}
                  onChange={(e) =>
                    handleFilterChange("seriesNo", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter series number"
                  min="1"
                  data-tooltip-id="filter-tooltip"
                  data-tooltip-content="Filter by series number"
                />
              </div>
              <div className="flex-1 min-w-[150px] flex flex-col gap-1">
                <label
                  htmlFor="dateFrom"
                  className="text-sm font-medium text-gray-700"
                >
                  Date Created At - From:
                </label>
                <input
                  id="dateFrom"
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) =>
                    handleFilterChange("dateFrom", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-tooltip-id="filter-tooltip"
                  data-tooltip-content="Filter by start date"
                />
              </div>
              <div className="flex-1 min-w-[150px] flex flex-col gap-1">
                <label
                  htmlFor="dateTo"
                  className="text-sm font-medium text-gray-700"
                >
                  Date Created At - To:
                </label>
                <input
                  id="dateTo"
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-tooltip-id="filter-tooltip"
                  data-tooltip-content="Filter by end date"
                />
              </div>
              <div className="flex-1 min-w-[150px] flex flex-col gap-1">
                <label
                  htmlFor="errorStatus"
                  className="text-sm font-medium text-gray-700"
                >
                  Error Status
                </label>
                <select
                  id="errorStatus"
                  value={filters.errorStatus}
                  onChange={(e) =>
                    handleFilterChange("errorStatus", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-tooltip-id="filter-tooltip"
                  data-tooltip-content="Filter by error status"
                >
                  <option value="all">All</option>
                  <option value="without_errors">Without Errors</option>
                  <option value="with_errors">With Errors</option>
                </select>
              </div>
              <div className="flex-1 min-w-[150px] flex flex-col gap-1">
                <label
                  htmlFor="cancelStatus"
                  className="text-sm font-medium text-gray-700"
                >
                  Cancellation Status
                </label>
                <select
                  id="cancelStatus"
                  value={filters.cancelStatus}
                  onChange={(e) =>
                    handleFilterChange("cancelStatus", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-tooltip-id="filter-tooltip"
                  data-tooltip-content="Filter by cancellation status"
                >
                  <option value="all">All</option>
                  <option value="not_cancelled">Not Cancelled</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Admin Fee Billing Series
            </h2>
            <button
              onClick={() => fetchSeriesHistory()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 mb-4"
              disabled={loading}
            >
              <FontAwesomeIcon
                icon={loading ? faSpinner : faSync}
                spin={loading}
              />
              Refresh
            </button>
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <LoadingSkeleton
                      rows={itemsPerPage}
                      columns={columns.length}
                    />
                  ) : seriesData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="p-3 text-center text-gray-500 text-sm"
                      >
                        No series found.
                      </td>
                    </tr>
                  ) : (
                    seriesData.map((series) => (
                      // <tr
                      //   key={series.series_no_uid}
                      //   className={`border-b hover:bg-gray-50 ${series.error_count > 0 ? 'bg-red-50' : ''} ${series.is_cancelled ? 'bg-gray-200' : ''}`}
                      // >
                      //   <td className="p-3 text-sm">
                      //     <input
                      //       type="checkbox"
                      //       checked={selectedRows.some((row) => row.series_no_uid === series.series_no_uid)}
                      //       onChange={() => handleRowSelect(series)}
                      //     />
                      //   </td>
                      //   <td className="p-3 text-sm">
                      //     {series.series_no}
                      //     {/* {!series.active && ( */}
                      //     {/* {series.is_cancelled && (
                      //       <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      //         CANCELLED
                      //       </span>
                      //     )} */}

                      //     {series.is_cancelled ? (
                      //       <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      //         CANCELLED
                      //       </span>
                      //     ) : null}

                      //   </td>
                      //   <td className="p-3 text-sm">{series.transaction_count}</td>
                      //   <td className="p-3 text-sm">{series.billing_type}</td>
                      //   <td className="p-3 text-sm">{phpFormatter.format(series.total_vat_amount)}</td>
                      //   <td className="p-3 text-sm">{phpFormatter.format(series.total_rate_amount)}</td>
                      //   <td className="p-3 text-sm">{phpFormatter.format(series.total_admin_fee_amount)}</td>
                      //   <td className="p-3 text-sm">{series.date_created}</td>
                      //   <td className="p-3 text-sm">
                      //     {series.delivery_date ? (
                      //       <span className="text-green-600">{series.delivery_date}</span>
                      //     ) : (
                      //       <span className="text-red-600">Not Yet Delivered</span>
                      //     )}
                      //   </td>
                      //   <td className="p-3 text-sm flex gap-2">
                      //     <Link
                      //       to={`/billing/admin-fee-billed/${series.series_no_uid}?series=${series.series_no}`}
                      //       className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                      //       data-tooltip-id="filter-tooltip"
                      //       data-tooltip-content="View data list on this series"
                      //     >
                      //       <FontAwesomeIcon icon={faEye} />
                      //     </Link>
                      //     <button
                      //       onClick={() => handleDownloadPdfs(series.series_no_uid, series.series_no)}
                      //       className="text-red-600 hover:text-red-800 flex items-center gap-1"
                      //       disabled={pdfLoading === series.series_no_uid}
                      //       data-tooltip-id="filter-tooltip"
                      //       data-tooltip-content="Export PDFs and Excel files"
                      //     >
                      //       <FontAwesomeIcon icon={pdfLoading === series.series_no_uid ? faSpinner : faDownload} spin={pdfLoading === series.series_no_uid} />
                      //     </button>
                      //     {series.error_count > 0 && (
                      //       <button
                      //         onClick={() => openErrorModal(series.series_no_uid)}
                      //         className="text-yellow-600 hover:text-yellow-800 flex items-center gap-1"
                      //         data-tooltip-id="filter-tooltip"
                      //         data-tooltip-content="View errors for this series"
                      //       >
                      //         <FontAwesomeIcon icon={faExclamationTriangle} />
                      //       </button>
                      //     )}
                      //     {!series.is_cancelled ? (
                      //       <button
                      //         onClick={() => openCancelModal(series.series_no, series.series_no_uid)}
                      //         className="text-red-600 hover:text-red-800 flex items-center gap-1"
                      //         data-tooltip-id="filter-tooltip"
                      //         data-tooltip-content="Cancel this series"
                      //       >
                      //         <FontAwesomeIcon icon={faBan} />
                      //       </button>
                      //     ) : (
                      //       <button
                      //         onClick={() => handleViewCancellation(series.series_no, series.series_no_uid)}
                      //         className="text-gray-600 hover:text-gray-800 flex items-center gap-1"
                      //         data-tooltip-id="filter-tooltip"
                      //         data-tooltip-content="View cancellation reason"
                      //       >
                      //         <FontAwesomeIcon icon={faInfoCircle} />
                      //       </button>
                      //     )}
                      //   </td>
                      // </tr>

                      // Update table row rendering to include corp_names
                      <tr
                        key={series.series_no_uid}
                        className={`border-b hover:bg-gray-50 ${
                          series.error_count > 0 ? "bg-red-50" : ""
                        } ${series.is_cancelled ? "bg-gray-200" : ""}`}
                      >
                        <td className="p-3 text-sm">
                          <input
                            type="checkbox"
                            checked={selectedRows.some(
                              (row) =>
                                row.series_no_uid === series.series_no_uid
                            )}
                            onChange={() => handleRowSelect(series)}
                          />
                        </td>
                        <td className="p-3 text-sm">
                          {filters.billingType == 'SOA' ? (
                            <>AR - </>
                          ) : null}
                          {series.series_no}
                          {series.is_cancelled ? (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              CANCELLED
                            </span>
                          ) : null}
                        </td>
                        <td className="p-3 text-sm">
                          {series.transaction_count}
                        </td>
                        <td className="p-3 text-sm">{series.billing_type}</td>
                        <td className="p-3 text-sm">
                          {series.corp_names}
                        </td>{" "}
                        {/* New column data */}
                        <td className="p-3 text-sm">
                          {phpFormatter.format(series.total_rate_amount)}
                        </td>
                        <td className="p-3 text-sm">
                          {phpFormatter.format(series.total_vat_amount)}
                        </td>
                        <td className="p-3 text-sm">
                          {phpFormatter.format(series.total_admin_fee_amount)}
                        </td>
                        <td className="p-3 text-sm">{series.date_created}</td>
                        <td className="p-3 text-sm">
                          {series.delivery_date ? (
                            <span className="text-green-600">
                              {series.delivery_date}
                            </span>
                          ) : (
                            <span className="text-red-600">
                              Not Yet Delivered
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-sm flex gap-2">
                          <Link
                            to={`/billing/admin-fee-billed/${series.series_no_uid}?series=${series.series_no}`}
                            className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                            data-tooltip-id="filter-tooltip"
                            data-tooltip-content="View data list on this series"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Link>
                          <button
                            onClick={() =>
                              handleDownloadPdfs(
                                series.series_no_uid,
                                series.series_no
                              )
                            }
                            className="text-red-600 hover:text-red-800 flex items-center gap-1"
                            disabled={pdfLoading === series.series_no_uid}
                            data-tooltip-id="filter-tooltip"
                            data-tooltip-content="Export PDFs and Excel files"
                          >
                            <FontAwesomeIcon
                              icon={
                                pdfLoading === series.series_no_uid
                                  ? faSpinner
                                  : faDownload
                              }
                              spin={pdfLoading === series.series_no_uid}
                            />
                          </button>
                          {series.error_count > 0 && (
                            <button
                              onClick={() =>
                                openErrorModal(series.series_no_uid)
                              }
                              className="text-yellow-600 hover:text-yellow-800 flex items-center gap-1"
                              data-tooltip-id="filter-tooltip"
                              data-tooltip-content="View errors for this series"
                            >
                              <FontAwesomeIcon icon={faExclamationTriangle} />
                            </button>
                          )}
                          {!series.is_cancelled ? (
                            <button
                              onClick={() =>
                                openCancelModal(
                                  series.series_no,
                                  series.series_no_uid
                                )
                              }
                              className="text-red-600 hover:text-red-800 flex items-center gap-1"
                              data-tooltip-id="filter-tooltip"
                              data-tooltip-content="Cancel this series"
                            >
                              <FontAwesomeIcon icon={faBan} />
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                handleViewCancellation(
                                  series.series_no,
                                  series.series_no_uid
                                )
                              }
                              className="text-gray-600 hover:text-gray-800 flex items-center gap-1"
                              data-tooltip-id="filter-tooltip"
                              data-tooltip-content="View cancellation reason"
                            >
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination and Action Buttons */}
          <div className="p-6 border-t border-gray-100">
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={handleDownloadTemplate}
                  className="px-3 py-1.5 text-xs text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-1 transition-colors"
                >
                  <FontAwesomeIcon icon={faDownload} /> Download Selected Series
                </button>
                <button
                  onClick={handleBatchUpload}
                  className="px-3 py-1.5 text-xs text-white bg-gray-600 rounded-lg hover:bg-gray-700 flex items-center gap-1 transition-colors"
                >
                  <FontAwesomeIcon icon={faUpload} /> Batch Upload Delivery Date
                </button>
              </div>
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex items-center gap-4 mb-3 md:mb-0">
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      handleItemsPerPageChange(Number(e.target.value))
                    }
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    aria-label="Items per page"
                  >
                    {[5, 10, 20, 50, 100].map((val) => (
                      <option key={val} value={val}>
                        {val} per page
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-600">
                    {getShowingEntriesText()}
                  </span>
                  <span className="text-sm text-gray-600">
                    Selected: {selectedRows.length} series
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1 || loading}
                    className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                    aria-label="First page"
                  >
                    <FontAwesomeIcon icon={faAngleDoubleLeft} />
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                    className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                    aria-label="Previous page"
                  >
                    <FontAwesomeIcon icon={faAngleLeft} />
                  </button>
                  {renderPageNumbers()}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                    className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                    aria-label="Next page"
                  >
                    <FontAwesomeIcon icon={faAngleRight} />
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages || loading}
                    className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                    aria-label="Last page"
                  >
                    <FontAwesomeIcon icon={faAngleDoubleRight} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Errors Modal */}
        {errorModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Series Errors
                  </h2>
                  <button
                    onClick={() => setErrorModalOpen(false)}
                    className="text-gray-600 hover:text-gray-800 text-lg"
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full table-auto">
                    <thead className="bg-gray-50">
                      <tr>
                        {errorColumns.map((column) => (
                          <th
                            key={column.key}
                            className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                          >
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {errorLoading ? (
                        <LoadingSkeleton
                          rows={errorPerPage}
                          columns={errorColumns.length}
                        />
                      ) : errorData.length === 0 ? (
                        <tr>
                          <td
                            colSpan={errorColumns.length}
                            className="p-3 text-center text-gray-500 text-sm"
                          >
                            No errors found.
                          </td>
                        </tr>
                      ) : (
                        errorData.map((error) => (
                          <tr
                            key={error.id}
                            className="border-b hover:bg-gray-50"
                          >
                            <td className="p-3 text-sm">{error.series_no}1</td>
                            <td className="p-3 text-sm">{error.corp_code}</td>
                            <td className="p-3 text-sm">{error.member_id}</td>
                            <td className="p-3 text-sm">{error.member_name}</td>
                            <td className="p-3 text-sm">
                              {error.member_type === "P"
                                ? "Principal"
                                : "Dependent"}
                            </td>
                            <td className="p-3 text-sm bg-red-50">
                              {error.error_message}
                            </td>
                            <td className="p-3 text-sm">{error.failed_at}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="p-6 border-t border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div className="flex items-center gap-4 mb-3 md:mb-0">
                    <select
                      value={errorPerPage}
                      onChange={(e) =>
                        handleErrorItemsPerPageChange(Number(e.target.value))
                      }
                      className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      aria-label="Errors per page"
                    >
                      {[5, 10, 20, 50].map((val) => (
                        <option key={val} value={val}>
                          {val} per page
                        </option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-600">
                      {getErrorShowingEntriesText()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleErrorPageChange(1)}
                      disabled={errorPage === 1 || errorLoading}
                      className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                      aria-label="First page"
                    >
                      <FontAwesomeIcon icon={faAngleDoubleLeft} />
                    </button>
                    <button
                      onClick={() => handleErrorPageChange(errorPage - 1)}
                      disabled={errorPage === 1 || errorLoading}
                      className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                      aria-label="Previous page"
                    >
                      <FontAwesomeIcon icon={faAngleLeft} />
                    </button>
                    {renderErrorPageNumbers()}
                    <button
                      onClick={() => handleErrorPageChange(errorPage + 1)}
                      disabled={errorPage === errorTotalPages || errorLoading}
                      className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                      aria-label="Next page"
                    >
                      <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                    <button
                      onClick={() => handleErrorPageChange(errorTotalPages)}
                      disabled={errorPage === errorTotalPages || errorLoading}
                      className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                      aria-label="Last page"
                    >
                      <FontAwesomeIcon icon={faAngleDoubleRight} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Series Modal */}
        <CancelSeriesModalAdminFee
          isOpen={cancelModalOpen}
          onClose={() => setCancelModalOpen(false)}
          onConfirm={handleCancelSeries}
          seriesNo={cancelSeriesNo}
          seriesNoUid={cancelSeriesNoUid}
          loading={cancelLoading}
        />

        {/* View Cancellation Modal */}
        <ViewCancellationModal
          isOpen={viewCancelModalOpen}
          onClose={() => setViewCancelModalOpen(false)}
          cancellationDetails={cancellationDetails}
          seriesNo={viewCancelSeriesNo}
        />

        {/* PDF Loading Indicator */}
        {pdfLoading && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-indigo-600 text-2xl animate-spin"
              />
              <p className="text-gray-800 text-lg">
                Please wait while generating the files...
              </p>
            </div>
          </div>
        )}

        {/* View Cancellation Loading Indicator */}
        {viewCancelLoading && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-indigo-600 text-2xl animate-spin"
              />
              <p className="text-gray-800 text-lg">
                Fetching cancellation details...
              </p>
            </div>
          </div>
        )}

        {/* Upload Delivery Dates Modal */}
        {uploadModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Upload Delivery Dates
                </h2>
                <button
                  onClick={() => setUploadModalOpen(false)}
                  className="text-gray-600 hover:text-gray-800 text-lg"
                >
                  ×
                </button>
              </div>
              <div className="flex flex-col gap-4">
                <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">
                    Excel File Format Instructions
                  </h3>
                  <p className="text-xs text-gray-600 mb-2">
                    Ensure your Excel file follows the template format with
                    these columns:
                  </p>
                  <ul className="list-disc list-inside text-xs text-gray-600 mb-2">
                    <li>
                      <strong>Series Unique ID</strong>: Unique identifier for
                      the series.
                    </li>
                    <li>
                      <strong>Series No</strong>: Series number.
                    </li>
                    <li>
                      <strong>Date Delivered</strong>: Delivery date in{" "}
                      <code>YYYY-MM-DD</code> format (e.g., 2025-12-31).
                    </li>
                  </ul>
                  <a
                    href={TEMPLATE_URL}
                    download="admin-fee-delivery-template.xlsx"
                    className="inline-flex items-center px-3 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs"
                    data-tooltip-id="filter-tooltip"
                    data-tooltip-content="Download Excel template"
                  >
                    <FontAwesomeIcon icon={faDownload} className="mr-2" />
                    Download Template
                  </a>
                </div>
                <div
                  className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-300 ${
                    isDragging
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-gray-300 bg-gray-50"
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <FontAwesomeIcon
                    icon={faUpload}
                    className="text-3xl text-indigo-500 mb-2"
                  />
                  <p className="text-sm text-gray-700">
                    Drag and drop Excel files here or{" "}
                    <button
                      type="button"
                      onClick={handleChooseFile}
                      className="text-indigo-600 font-semibold hover:text-indigo-800"
                    >
                      choose files
                    </button>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Supported formats: .xlsx, .xls (Max {maxFileSizeMB}MB per
                    file, up to {maxUploadLimit} files)
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    accept=".xlsx,.xls"
                    onChange={(e) => handleFileChange(e.target.files)}
                    className="hidden"
                  />
                </div>
                {files.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">
                      Selected Files
                    </h3>
                    <div className="flex justify-end gap-2 mb-4">
                      <button
                        onClick={() => setUploadModalOpen(false)}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                        disabled={isUploading}
                      >
                        Close
                      </button>
                    </div>
                    <ul className="space-y-2">
                      {files.map((fileObj, index) => (
                        <li
                          key={index}
                          className="flex items-center justify-between bg-gray-100 p-3 rounded-lg"
                        >
                          <div className="flex-1">
                            <p className="text-xs font-medium text-gray-900 truncate">
                              {fileObj.file.name}
                            </p>
                            {fileObj.isUploading && (
                              <div className="relative pt-1">
                                <div className="bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                  <div
                                    className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300"
                                    style={{ width: `${fileObj.progress}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-600 mt-1 block">
                                  {fileObj.progress}% Uploading
                                </span>
                              </div>
                            )}
                            {fileObj.isPolling && (
                              <div className="flex items-center gap-2 mt-1">
                                <FontAwesomeIcon
                                  icon={faSpinner}
                                  className="text-indigo-600 text-xs animate-spin"
                                />
                                <span className="text-xs text-gray-600">
                                  Processing...
                                </span>
                              </div>
                            )}
                            {fileObj.error && (
                              <p className="text-xs text-red-600 mt-1">
                                Error: {fileObj.error}
                              </p>
                            )}
                            {fileObj.status && !fileObj.isPolling && (
                              <div className="text-xs mt-1">
                                <p
                                  className={
                                    fileObj.status === "completed"
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }
                                >
                                  Status:{" "}
                                  {fileObj.status === "completed"
                                    ? "Completed"
                                    : fileObj.status === "failed"
                                    ? "Failed"
                                    : "Timeout"}
                                </p>
                                {fileObj.summary && (
                                  <>
                                    <p>
                                      Successful Updates:{" "}
                                      {fileObj.summary.successCount || 0}
                                    </p>
                                    <p>
                                      Failed Updates:{" "}
                                      {fileObj.summary.failedCount || 0}
                                    </p>
                                    {fileObj.summary.errorMessage && (
                                      <p className="text-red-600">
                                        Error: {fileObj.summary.errorMessage}
                                      </p>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveFile(fileObj)}
                            className="text-red-500 hover:text-red-700"
                            disabled={fileObj.isUploading || fileObj.isPolling}
                            data-tooltip-id="filter-tooltip"
                            data-tooltip-content="Remove file"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => setUploadModalOpen(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                    disabled={isUploading}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFeeHistory;
